"use client";

import { useEffect } from "react";
import styles from "./StoreStock.module.scss";
import classNames from "classnames/bind";
import { useOnlineStock } from "../../../hooks/useOnlineStock/useOnlineStock";
import { SIZE_SELECTOR_ERROR_NO_SIZE_SELECTED } from "../../../constants/constants";
import { useStoreStockInfoAvailability } from "./hooks/useStoreStockInfoAvailability";

const cx = classNames.bind(styles);

declare global {
  interface Window {
    isSizeSelected: () => boolean;
  }
}

export const ONLINE_STOCK_AVAILABILITY_STATUS_SPECIAL_ORDER =
  "SKU_AVAILABLE_FOR_SPECIAL_ORDER";

export type Props = {
  displayCode: string;
  isFutureRelease: boolean;
  isPreOrderable: boolean;
  skuId: string;
  stockApiBaseUrl: string;
  stockApiKey: string;
  storeStockApiKey: string;
  storeStockModalApiUrl: string;
  storeStockScriptUrl: string;
  title: string;
  usePrimaryButton: boolean;
  hideIfStoreStockUnavailable?: boolean;
  isVariantSelected?: boolean;
};

export const StoreStock = ({
  title,
  displayCode,
  skuId,
  isFutureRelease,
  isPreOrderable,
  usePrimaryButton,
  storeStockScriptUrl,
  stockApiBaseUrl,
  stockApiKey,
  storeStockApiKey,
  storeStockModalApiUrl,
  isVariantSelected = false,
  hideIfStoreStockUnavailable = true,
}: Props) => {
  const onlineStock = useOnlineStock(skuId, stockApiBaseUrl, stockApiKey);
  const storeStockInfoAvailability = useStoreStockInfoAvailability(
    displayCode,
    storeStockModalApiUrl,
    stockApiBaseUrl,
    storeStockApiKey,
  );

  useEffect(() => {
    window.isSizeSelected = () => {
      if (!isVariantSelected) {
        window.dispatchEvent(
          new CustomEvent(SIZE_SELECTOR_ERROR_NO_SIZE_SELECTED),
        );
        return false;
      } else {
        return true;
      }
    };
  }, [isVariantSelected]);

  const showStoreStockButton = () => {
    if (isFutureRelease || isPreOrderable) {
      return false;
    }

    if (onlineStock.isLoading || storeStockInfoAvailability.isLoading) {
      return false;
    }

    if (
      onlineStock.data?.availabilityStatus ===
      ONLINE_STOCK_AVAILABILITY_STATUS_SPECIAL_ORDER
    ) {
      return false;
    }

    if (hideIfStoreStockUnavailable) {
      if (!storeStockInfoAvailability.error) {
        return !!storeStockInfoAvailability.data?.[displayCode];
      }
    }

    return true;
  };

  useEffect(() => {
    const jlStoreStockEl = document.createElement("jl-store-stock");
    jlStoreStockEl.setAttribute("data-testid", "store-stock");
    jlStoreStockEl.setAttribute("productCode", displayCode);
    jlStoreStockEl.setAttribute("productName", title);
    jlStoreStockEl.setAttribute("skuId", skuId);
    jlStoreStockEl.setAttribute("isPrimaryButton", String(usePrimaryButton));
    document.body.appendChild(jlStoreStockEl);

    const jlStoreStockScript = document.createElement("script");
    jlStoreStockScript.setAttribute("type", "text/javascript");
    jlStoreStockScript.setAttribute("src", storeStockScriptUrl);

    document.body.appendChild(jlStoreStockScript);

    return () => {
      if (document.body.contains(jlStoreStockEl)) {
        document.body.removeChild(jlStoreStockEl);
      }
      if (document.body.contains(jlStoreStockScript)) {
        document.body.removeChild(jlStoreStockScript);
      }
    };
  }, []);

  return (
    <div
      id="product:tools:store-stock"
      data-testid="product:tools:store-stock"
      aria-busy={onlineStock.isLoading || storeStockInfoAvailability.isLoading}
      className={cx({
        hidden: !showStoreStockButton(),
      })}
    >
      <div
        id="store-stock-button-wrapper"
        data-testid="product:tools:store-stock:button"
      />
    </div>
  );
};
